import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Lock, Check, X, Flame, Store, Globe, Sparkles } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useProductContext, type ProductType } from "../context/ProductContext";
import { useUserContext } from "../context/UserContext";
import { getRetailerAccessOverride, hasUserRetailerAccess, isFreeTier } from "../utils/userUtils";
import { useTranslation } from "../context/LanguageContext";
import { track } from "../utils/analytics";
import {
  hasBaselineOnlineModeAccess,
  isBaselineOnlineProductType,
} from "./SidebarRetailSelector.utils";
import { isExtraOnlineRetailer, isOnlineOnlyRetailer, isRetailerClientEnabled } from "../config/retailers";
import PlanUpgradeSheet from "./PlanUpgradeSheet";
import { BEST_DEALS_ITEM, hasBestDealsAccess, moreStoresPlanName, type LockedItem } from "../utils/planUpgrade";

interface SidebarRetailSelectorProps {
  className?: string;
  compact?: boolean;
  withMargin?: boolean;
  variant?: "default" | "light";
  /** Icon-only trigger with a right-side flyout, for the collapsed sidebar rail. */
  iconOnly?: boolean;
  /** Full-height icon trigger used inside the mobile bottom navigation. */
  bottomNavTrigger?: boolean;
}

const baseProductOptions: { value: ProductType; label: string; color: string }[] = [
  { value: "walmart", label: "Walmart", color: "#0071CE" },
  { value: "walmart_online", label: "Walmart Online", color: "#0071CE" },
  { value: "amazon", label: "Amazon", color: "#FF9900" },
  { value: "lowes", label: "Lowe's", color: "#004990" },
  { value: "tractor_supply", label: "Tractor Supply", color: "#B31B34" },
  { value: "northern_tool", label: "Northern Tool", color: "#D71920" },
  { value: "samsclub", label: "Sam's Club", color: "#0067A0" },
  { value: "ace_hardware", label: "Ace Hardware", color: "#A6192E" },
  { value: "kohls", label: "Kohl's", color: "#5E2B97" },
  { value: "home_depot", label: "HD", color: "#F96302" },
  { value: "target", label: "Target", color: "#CC0000" },
  { value: "dollar_general", label: "Dollar General", color: "#FFD100" },
  // CVS client pilot paused.
  { value: "woot", label: "Woot", color: "#7AC143" },
  { value: "bestbuy", label: "Best Buy", color: "#0046BE" },
  { value: "costco", label: "Costco", color: "#E31837" },
  { value: "newegg", label: "Newegg", color: "#E87511" },
  { value: "gamestop", label: "GameStop", color: "#E31837" },
  { value: "bjs", label: "BJ's Wholesale", color: "#E4002B" },
  { value: "meijer", label: "Meijer", color: "#D5202B" },
  { value: "menards", label: "Menards", color: "#007A33" },
  { value: "academy", label: "Academy Sports", color: "#003087" },
  { value: "walgreens", label: "Walgreens", color: "#E31837" },
  { value: "zappos", label: "Zappos", color: "#0071BC" },
  { value: "jcpenney", label: "JCPenney", color: "#CE0E2D" },
  { value: "belk", label: "Belk", color: "#00A6A0" },
  { value: "michaels", label: "Michaels", color: "#E4002B" },
  { value: "nike", label: "Nike", color: "#111111" },
  { value: "adidas", label: "Adidas", color: "#111111" },
  { value: "reebok", label: "Reebok", color: "#E01A2B" },
  { value: "skechers", label: "Skechers", color: "#003D7D" },
  { value: "puma", label: "Puma", color: "#C8102E" },
  { value: "underarmour", label: "Under Armour", color: "#1D1D1D" },
  { value: "columbia", label: "Columbia", color: "#003DA5" },
  { value: "nordstromrack", label: "Nordstrom Rack", color: "#C8102E" },
  { value: "aldo", label: "Aldo", color: "#111111" },
  { value: "vitacost", label: "Vitacost", color: "#6CB33F" },
  { value: "journeys", label: "Journeys", color: "#111111" },
  { value: "shopdisney", label: "shopDisney", color: "#1A6FB5" },
  { value: "ninjakitchen", label: "Ninja Kitchen", color: "#D91E2A" },
  { value: "hottopic", label: "Hot Topic", color: "#E4002B" },
  { value: "officedepot", label: "Office Depot", color: "#D6002B" },
  { value: "barnesnoble", label: "Barnes & Noble", color: "#00704A" },
  { value: "razer", label: "Razer", color: "#44D62C" },
  { value: "pacsun", label: "PacSun", color: "#111111" },
  { value: "bathbodyworks", label: "Bath & Body Works", color: "#003D5B" },
  { value: "staples", label: "Staples", color: "#CC0000" },
  { value: "petsmart", label: "PetSmart", color: "#0056A6" },
  { value: "ulta", label: "Ulta Beauty", color: "#E85C0D" },
  { value: "forever21", label: "Forever 21", color: "#111111" },
  { value: "gap", label: "Gap", color: "#001F5B" },
  { value: "petco", label: "Petco", color: "#005CB9" },
  { value: "americaneagle", label: "American Eagle", color: "#1F3A5F" },
  { value: "aerie", label: "Aerie", color: "#6F6259" },
  { value: "abercrombie", label: "Abercrombie & Fitch", color: "#1D1D1B" },
  { value: "hollister", label: "Hollister", color: "#734B32" },
  { value: "oldnavy", label: "Old Navy", color: "#003764" },
  { value: "bananarepublic", label: "Banana Republic", color: "#2B2A27" },
  { value: "stevemadden", label: "Steve Madden", color: "#D71920" },
  { value: "athleta", label: "Athleta", color: "#556B5D" },
  { value: "gapfactory", label: "Gap Factory", color: "#001F5B" },
  { value: "bananarepublicfactory", label: "Banana Republic Factory", color: "#403F3C" },
  { value: "boxlunch", label: "BoxLunch", color: "#E31837" },
  { value: "verabradley", label: "Vera Bradley", color: "#5C2D91" },
  { value: "footlocker", label: "Foot Locker", color: "#D71920" },
  { value: "champs", label: "Champs Sports", color: "#E31837" },
];

const getProductOptions = () =>
  baseProductOptions.filter((option) => isRetailerClientEnabled(option.value));

// Brightened stand-ins for brand colors too dark to read as a dot on the dark sidebar
const dotColorOverrides: Partial<Record<ProductType, string>> = {
  lowes: "#2273CF",
  bestbuy: "#2E6BFF",
  kohls: "#9155E0",
  menards: "#17A24A",
  academy: "#2E6BFF",
  nike: "#B0B0B0",
  adidas: "#B0B0B0",
  underarmour: "#C8102E",
  aldo: "#B0B0B0",
  journeys: "#B0B0B0",
  pacsun: "#B0B0B0",
  bathbodyworks: "#2E8BC0",
};

function BrandDot({
  retailer,
  size = 8,
  locked = false,
}: {
  retailer: ProductType;
  size?: number;
  locked?: boolean;
}) {
  const color =
    dotColorOverrides[retailer] ??
    baseProductOptions.find((o) => o.value === retailer)?.color ??
    "#8B8A8C";
  return (
    <span aria-hidden className="flex w-4 items-center justify-center flex-shrink-0">
      <span
        className={`rounded-full transition-all duration-150 ${locked ? "opacity-40 saturate-0" : ""}`}
        style={{
          width: size,
          height: size,
          background: color,
          boxShadow: locked ? "none" : `0 0 0 ${Math.max(2, Math.round(size / 3))}px ${color}29`,
        }}
      />
    </span>
  );
}

function FeedGlyph({
  icon: Icon,
  size = 15,
  dimmed = false,
}: {
  icon: typeof Flame;
  size?: number;
  dimmed?: boolean;
}) {
  return (
    <span aria-hidden className="flex w-4 items-center justify-center flex-shrink-0">
      <Icon size={size} strokeWidth={2.25} className={dimmed ? "text-primary-500/50 dark:text-dark-500" : "text-violet-300"} />
    </span>
  );
}

type RetailerMode = "in_store" | "online";

const retailerModeMap: Partial<Record<ProductType, RetailerMode[]>> = {
  walmart: ["in_store", "online"],
  walmart_online: ["online"],
  amazon: ["online"],
  lowes: ["in_store", "online"],
  tractor_supply: ["in_store", "online"],
  northern_tool: ["in_store", "online"],
  samsclub: ["in_store"],
  ace_hardware: ["online"],
  kohls: ["online"],
  home_depot: ["in_store", "online"],
  target: ["in_store", "online"],
  target_online: ["online"],
  dollar_general: ["in_store"],
  woot: ["online"],
  bestbuy: ["in_store", "online"],
  costco: ["in_store", "online"],
  newegg: ["online"],
  gamestop: ["online"],
  bjs: ["online"],
  meijer: ["online"],
  menards: ["online"],
  academy: ["online"],
  walgreens: ["online"],
  zappos: ["online"],
  jcpenney: ["online"],
  belk: ["online"],
  michaels: ["online"],
  nike: ["online"],
  adidas: ["online"],
  reebok: ["online"],
  skechers: ["online"],
  puma: ["online"],
  underarmour: ["online"],
  columbia: ["online"],
  nordstromrack: ["online"],
  aldo: ["online"],
  vitacost: ["online"],
  journeys: ["online"],
  shopdisney: ["online"],
  ninjakitchen: ["online"],
  hottopic: ["online"],
  officedepot: ["online"],
  barnesnoble: ["online"],
  razer: ["online"],
  pacsun: ["online"],
  bathbodyworks: ["online"],
  staples: ["online"],
};

const supportsRetailerMode = (retailer: ProductType, mode: RetailerMode) =>
  isOnlineOnlyRetailer(retailer)
    ? mode === "online"
    : retailerModeMap[retailer]?.includes(mode) ?? mode === "in_store";

export default function SidebarRetailSelector({
  className = "",
  compact = false,
  withMargin = false,
  variant = "default",
  iconOnly = false,
  bottomNavTrigger = false,
}: SidebarRetailSelectorProps) {
  const { selectedProduct, setSelectedProduct } = useProductContext();
  const { user } = useUserContext();
  const isFreeUser = isFreeTier(user);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const productOptions = getProductOptions();
  const [open, setOpen] = useState(false);
  const [activeMode, setActiveMode] = useState<RetailerMode>("in_store");
  const [sheetVisible, setSheetVisible] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Paid users get the plan comparison sheet for what they tapped instead of the generic modal.
  const [planSheetItem, setPlanSheetItem] = useState<LockedItem | null>(null);
  const [showHomeDepotModal, setShowHomeDepotModal] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sheetListRef = useRef<HTMLDivElement>(null);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);

  // Close desktop dropdown on outside click
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  // Animate bottom sheet in
  useEffect(() => {
    if (open) requestAnimationFrame(() => setSheetVisible(true));
    else setSheetVisible(false);
  }, [open]);

  const closeSheet = () => { setSheetVisible(false); setTimeout(() => setOpen(false), 200); };

  const hasHomeDepot = () => isFreeUser || hasUserRetailerAccess(user, "home_depot");

  const trackRetailerNavigation = (
    destinationPath: string,
    tabLabel: string,
    extra?: Record<string, unknown>
  ) => {
    track("navigation-clicked", {
      tab: destinationPath,
      tabLabel,
      navigationType: "sidebar-retailer-selector",
      fromPath: location.pathname,
      selectedProduct,
      activeMode,
      ...extra,
    });
  };

  const checkAccess = (retailer: ProductType) => {
    const overridden = getRetailerAccessOverride(user, retailer);
    if (isFreeUser) {
      return isBaselineOnlineProductType(retailer);
    }
    if (overridden !== undefined) return overridden;
    return hasUserRetailerAccess(user, retailer);
  };

  const handleRetailerClick = (retailer: ProductType) => {
    if (!checkAccess(retailer)) {
      if (retailer === "home_depot") setShowHomeDepotModal(true);
      else denyAccess(retailer);
      setOpen(false);
      return;
    }
    if (isFreeUser) {
      setSelectedProduct(retailer);
      trackRetailerNavigation(`/${retailer}/products`, optionLabel(retailer));
      navigate(`/${retailer}/products`, { replace: true });
      setOpen(false);
      return;
    }
    setSelectedProduct(retailer);
    trackRetailerNavigation(`/${retailer}/products`, optionLabel(retailer));
    navigate(`/${retailer}/products`, { replace: true });
    setOpen(false);
  };

  const isFeaturedDealsActive = location.pathname === "/hot-deals";
  const isHawkFindsActive = location.pathname === "/hawk-finds";
  const isArbitrageActive = location.pathname === "/flips";
  const selectedStoreId = new URLSearchParams(location.search).get("storeId");
  const isOnlineStoreMode = isFreeUser || selectedStoreId === "online";
  const isWalmartOnlineActive =
    !isFeaturedDealsActive &&
    !isHawkFindsActive &&
    selectedProduct === "walmart" &&
    isOnlineStoreMode;
  const isHomeDepotOnlineActive =
    !isFeaturedDealsActive &&
    !isHawkFindsActive &&
    selectedProduct === "home_depot" &&
    isOnlineStoreMode;
  const retailerListOptions = productOptions.filter((option) => option.value !== "walmart_online");
  const retailersForActiveMode = retailerListOptions.filter((option) => {
    if (!supportsRetailerMode(option.value, activeMode)) return false;
    return true;
  });
  const canOpenRetailerSelector = productOptions.length > 1;
  const handleUpgradeClick = (locked?: LockedItem) => {
    setShowUpgradeModal(false);
    setPlanSheetItem(null);
    trackRetailerNavigation("/settings?tab=billing&upgradeSource=sidebar", t("products.viewPlans"), {
      source: locked ? "sidebar-retailer-selector-plan-sheet" : "sidebar-retailer-selector-upgrade",
      ...(locked ? { locked } : {}),
    });
    navigate("/settings?tab=billing&upgradeSource=sidebar");
  };
  // Best Deals is a Pro feed (same rule as its page); free accounts keep their own locked treatment.
  const hasHotDealsAccess = isFreeUser || hasBestDealsAccess(user);
  const goToFeaturedDeals = () => {
    if (isFreeUser || !hasHotDealsAccess) {
      denyAccess(BEST_DEALS_ITEM);
      setOpen(false);
      return;
    }
    trackRetailerNavigation("/hot-deals", t("products.hotDealsLabel"));
    navigate("/hot-deals");
    setOpen(false);
  };
  const upgradeTitle = isFreeUser ? t("products.unlockInStoreAccess") : t("products.upgradeRequired");
  const upgradeMessage = isFreeUser
    ? t("products.freeUpgradeMessage")
    : t("products.paidUpgradeMessage");

  const isModeSelected = (retailer: ProductType, mode: RetailerMode) => {
    if (isFeaturedDealsActive || isHawkFindsActive || isArbitrageActive) return false;
    const isRetailerMatch = selectedProduct === retailer;
    if (retailer === "woot") return mode === "online" && isRetailerMatch;
    if (retailer === "walmart") {
      return mode === "online" ? isWalmartOnlineActive : isRetailerMatch && !isWalmartOnlineActive;
    }
    if (retailer === "home_depot") {
      return mode === "online" ? isHomeDepotOnlineActive : isRetailerMatch && !isHomeDepotOnlineActive;
    }
    return mode === "online"
      ? isRetailerMatch && isOnlineStoreMode
      : isRetailerMatch && !isOnlineStoreMode;
  };

  const optionLabel = (retailer: ProductType) =>
    productOptions.find((option) => option.value === retailer)?.label || retailer;

  const hasModeAccess = (retailer: ProductType, mode: RetailerMode) => {
    if (!isFreeUser && getRetailerAccessOverride(user, retailer) === false) {
      return false;
    }
    if (hasBaselineOnlineModeAccess(retailer, mode)) {
      return true;
    }
    if (isFreeUser) {
      if (mode === "in_store") {
        return false;
      }
      if (retailer === "walmart") return checkAccess("walmart_online");
      return checkAccess(retailer);
    }
    if (mode === "online" && retailer === "walmart") return checkAccess("walmart_online");
    if (retailer === "home_depot") return mode === "online" ? true : hasHomeDepot();
    return checkAccess(retailer);
  };

  /** Free accounts get the generic upgrade modal; paid plans see what the next plan adds. */
  function denyAccess(item: LockedItem) {
    if (isFreeUser) setShowUpgradeModal(true);
    else setPlanSheetItem(item);
  }

  const closePlanSheet = useCallback(() => setPlanSheetItem(null), []);

  const closeSelector = (large: boolean) => {
    if (large) closeSheet();
    else setOpen(false);
  };

  const handleRetailerModeClick = (retailer: ProductType, mode: RetailerMode, large: boolean) => {
    if (!hasModeAccess(retailer, mode)) {
      denyAccess(retailer);
      closeSelector(large);
      return;
    }
    if (retailer === "walmart") {
      if (mode !== "online" && !checkAccess("walmart")) {
        denyAccess(retailer);
        closeSelector(large);
        return;
      }
      setSelectedProduct("walmart");
      trackRetailerNavigation(
        mode === "online" ? "/walmart/products?storeId=online" : "/walmart/products?storeId=all",
        mode === "online" ? "Walmart Online" : "Walmart",
        { retailer: "walmart", mode }
      );
      navigate(
        mode === "online" ? "/walmart/products?storeId=online" : "/walmart/products?storeId=all",
        { replace: true }
      );
      closeSelector(large);
      return;
    }
    if (retailer === "home_depot") {
      if (mode !== "online" && !hasHomeDepot()) {
        setShowHomeDepotModal(true);
        closeSelector(large);
        return;
      }
      setSelectedProduct("home_depot");
      trackRetailerNavigation(
        mode === "online" ? "/home_depot/products?storeId=online" : "/home_depot/products?storeId=all",
        mode === "online" ? "HD Online" : "HD",
        { retailer: "home_depot", mode }
      );
      navigate(
        mode === "online" ? "/home_depot/products?storeId=online" : "/home_depot/products?storeId=all",
        { replace: true }
      );
      closeSelector(large);
      return;
    }
    if (mode === "online") {
      if (hasBaselineOnlineModeAccess(retailer, mode) && (isFreeUser || getRetailerAccessOverride(user, retailer) !== false)) {
        setSelectedProduct(retailer);
        trackRetailerNavigation(`/${retailer}/products?storeId=online`, optionLabel(retailer), {
          retailer,
          mode,
        });
        navigate(`/${retailer}/products?storeId=online`, { replace: true });
        closeSelector(large);
        return;
      }
      if (!checkAccess(retailer)) {
        denyAccess(retailer);
        closeSelector(large);
        return;
      }
      if (isFreeUser) {
        setSelectedProduct(retailer);
        navigate(`/${retailer}/products?storeId=online`, { replace: true });
        closeSelector(large);
        return;
      }
      if (!checkAccess(retailer)) {
        trackRetailerNavigation("/settings", optionLabel(retailer), {
          retailer,
          mode,
          accessFallback: "settings",
        });
        navigate("/settings");
        closeSelector(large);
        return;
      }
      setSelectedProduct(retailer);
      trackRetailerNavigation(`/${retailer}/products?storeId=online`, optionLabel(retailer), {
        retailer,
        mode,
      });
      navigate(`/${retailer}/products?storeId=online`, { replace: true });
      closeSelector(large);
      return;
    }
    handleRetailerClick(retailer);
    closeSelector(large);
  };

  useEffect(() => {
    if (!open) return;
    if (isFreeUser || selectedProduct === "woot" || selectedProduct === "kohls" || selectedStoreId === "online") {
      setActiveMode("online");
      return;
    }
    if (selectedProduct === "dollar_general") {
      setActiveMode("in_store");
    }
  }, [isFreeUser, open, selectedProduct, selectedStoreId]);

  if (!user) return null;

  const currentOption = productOptions.find((o) => o.value === selectedProduct);
  const isFeedActive = isFeaturedDealsActive || isHawkFindsActive || isArbitrageActive;
  const currentLabel = isFeaturedDealsActive
    ? t("products.hotDealsLabel")
    : isArbitrageActive
    ? "Flips"
    : isHawkFindsActive
    ? t("products.hawkFindsLabel")
    : currentOption &&
      (currentOption.value === "amazon" || currentOption.value === "kohls" || isExtraOnlineRetailer(currentOption.value))
    ? currentOption.label
    : isOnlineStoreMode && currentOption
    ? `${currentOption.label} Online`
    : currentOption?.label || "Walmart";

  const triggerTitle = isFeaturedDealsActive
    ? t("products.hotDealsLabel")
    : isArbitrageActive
    ? "Flips"
    : isHawkFindsActive
    ? t("products.hawkFindsLabel")
    : currentOption?.label || "Walmart";

  const currentValue = currentOption?.value ?? "walmart";
  const triggerCaption = isFeedActive
    ? null
    : !supportsRetailerMode(currentValue, "in_store")
    ? t("products.onlineTab")
    : !supportsRetailerMode(currentValue, "online")
    ? t("products.inStoreTab")
    : isOnlineStoreMode
    ? t("products.onlineTab")
    : t("products.inStoreTab");

  const RetailerModeTabs = ({ size = "normal" }: { size?: "normal" | "large" }) => {
    const lg = size === "large";
    const isOnline = activeMode === "online";
    const segmentClass = (mode: RetailerMode) =>
      `relative z-10 flex-1 inline-flex items-center justify-center gap-1.5 font-semibold rounded-full transition-colors duration-200 cursor-pointer ${
        lg ? "py-2 text-sm" : "py-1.5 text-xs"
      } ${activeMode === mode ? "text-white" : "text-primary-400 hover:text-primary-200"}`;
    const selectMode = (mode: RetailerMode) => {
      setActiveMode(mode);
      sheetListRef.current?.scrollTo(0, 0);
    };
    return (
      <div className={lg ? "px-4 pt-3 pb-2" : "px-2 pt-2 pb-1.5"}>
        <div className="relative flex items-center rounded-full bg-black/25 dark:bg-black/30 p-1 ring-1 ring-white/[0.06]">
          <span
            aria-hidden
            className={`absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-full bg-white/15 ring-1 ring-white/10 shadow-sm transition-transform duration-200 ease-out ${
              isOnline ? "translate-x-full" : "translate-x-0"
            }`}
          />
          <button
            onClick={(e) => { e.stopPropagation(); selectMode("in_store"); }}
            className={segmentClass("in_store")}
          >
            <Store size={lg ? 14 : 12} />
            {t("products.inStoreTab")}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); selectMode("online"); }}
            className={segmentClass("online")}
          >
            <Globe size={lg ? 14 : 12} />
            {t("products.onlineTab")}
          </button>
        </div>
      </div>
    );
  };

  const RetailerRow = ({
    option,
    size = "normal",
    index = 0,
  }: {
    option: typeof baseProductOptions[0];
    size?: "normal" | "large";
    index?: number;
  }) => {
    const lg = size === "large";
    const isSelected = isModeSelected(option.value, activeMode);
    const hasAccess = hasModeAccess(option.value, activeMode);
    const isFreeInStoreLocked = isFreeUser && activeMode === "in_store" && !hasAccess;
    const activate = (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleRetailerModeClick(option.value, activeMode, lg);
    };
    return (
      <button
        key={option.value}
        title={option.label}
        // In the sheet, activate on click so a swipe starting on a row doesn't navigate
        onPointerDown={lg ? undefined : activate}
        onClick={lg ? activate : undefined}
        className={`group w-full flex items-center animate-selector-row-in cursor-pointer text-left transition-all duration-150 ${
          lg ? "gap-2.5 min-h-[46px] px-3 py-2 rounded-xl" : "gap-2.5 px-2 py-1.5 rounded-lg"
        } ${
          isSelected
            ? "bg-white/[0.09] ring-1 ring-white/10"
            : hasAccess
            ? "hover:bg-white/[0.05] active:bg-white/[0.08]"
            : "hover:bg-white/[0.025]"
        }`}
        style={{ animationDelay: `${Math.min(index, 12) * 16}ms` }}
      >
        <BrandDot retailer={option.value} size={lg ? 9 : 8} locked={!hasAccess} />
        <span
          className={`flex-1 truncate font-medium ${lg ? "text-[14px]" : "text-[13px]"} ${
            hasAccess ? "text-primary-100 dark:text-dark-200" : "text-primary-500/60 dark:text-dark-500"
          }`}
        >
          {option.label}
        </span>
        {isFreeInStoreLocked && (
          <span className="rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-amber-300/90 flex-shrink-0">
            {t("products.lockedLabel")}
          </span>
        )}
        {isSelected && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-white flex-shrink-0">
            <Check size={12} strokeWidth={3} />
          </span>
        )}
      </button>
    );
  };

  // The plan's stores first, then the ones an upgrade unlocks (pinto order).
  const unlockedRetailers = retailersForActiveMode.filter((option) => hasModeAccess(option.value, activeMode));
  const lockedRetailers = retailersForActiveMode.filter((option) => !hasModeAccess(option.value, activeMode));

  const MoreStoresHeader = ({ size = "normal" }: { size?: "normal" | "large" }) => {
    const lg = size === "large";
    const openUpgrade = (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const first = lockedRetailers[0]?.value;
      if (first) handleRetailerModeClick(first, activeMode, lg);
    };
    return (
      <div
        className={`col-span-full flex items-center justify-between gap-2 ${
          lg ? "mt-3 mb-0.5 px-1" : "mt-1.5 mb-0.5 px-2"
        }`}
      >
        <p
          className={`truncate font-semibold text-primary-400/60 dark:text-dark-500 uppercase tracking-widest ${
            lg ? "text-[10px]" : "text-[9px]"
          }`}
        >
          {t("storeMenu.moreStoresWith", { plan: moreStoresPlanName(user?.retailers) })}
        </p>
        <button
          type="button"
          onPointerDown={lg ? undefined : openUpgrade}
          onClick={lg ? openUpgrade : undefined}
          className={`flex-shrink-0 font-semibold text-violet-300 hover:text-violet-200 transition-colors cursor-pointer ${
            lg ? "text-[13px]" : "text-[12px]"
          }`}
        >
          {t("storeMenu.upgradeLink")}
        </button>
      </div>
    );
  };

  const FeaturedDealsItem = ({ size = "normal" }: { size?: "normal" | "large" }) => {
    const lg = size === "large";
    const activate = (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      goToFeaturedDeals();
    };
    return (
      <button
        onPointerDown={lg ? undefined : activate}
        onClick={lg ? activate : undefined}
        className={`group w-full flex items-center animate-selector-row-in cursor-pointer text-left transition-all duration-150 ${
          lg ? "gap-2.5 min-h-[46px] px-3 py-2 rounded-xl" : "gap-2.5 px-2 py-1.5 rounded-lg"
        } ${
          isFeaturedDealsActive
            ? "bg-white/[0.09] ring-1 ring-white/10"
            : isFreeUser || !hasHotDealsAccess
            ? "hover:bg-white/[0.025]"
            : "hover:bg-white/[0.05] active:bg-white/[0.08]"
        }`}
      >
        <FeedGlyph icon={Flame} size={lg ? 16 : 15} dimmed={isFreeUser || !hasHotDealsAccess} />
        <span
          className={`flex-1 truncate font-medium ${lg ? "text-[14px]" : "text-[13px]"} ${
            isFreeUser || !hasHotDealsAccess ? "text-primary-500/60 dark:text-dark-500" : "text-primary-100 dark:text-dark-200"
          }`}
        >
          {t("products.hotDealsLabel")}
        </span>
        {isFeaturedDealsActive && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-white flex-shrink-0">
            <Check size={12} strokeWidth={3} />
          </span>
        )}
        {isFreeUser && !isFeaturedDealsActive && (
          <Lock size={lg ? 13 : 11} className="text-primary-500/50 dark:text-dark-500 flex-shrink-0" />
        )}
        {!isFreeUser && !hasHotDealsAccess && !isFeaturedDealsActive && (
          <span className={`flex-shrink-0 font-bold text-violet-300/90 dark:text-violet-300/80 ${lg ? "text-[12px]" : "text-[11px]"}`}>
            {t("storeMenu.proLabel")}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={`relative min-w-0 ${bottomNavTrigger ? "h-full" : ""} ${className} ${withMargin ? "mx-3" : ""}`}
      >
        <div className={bottomNavTrigger ? "h-full" : "py-2"}>
          <button
            onPointerDown={(event) => {
              event.stopPropagation();
              // The bottom-nav sheet must not mount until the opening tap has
              // completed. Opening it on pointer-down lets the matching
              // pointer-up land on a retailer row underneath the user's
              // finger and immediately navigate away.
              if (!bottomNavTrigger && canOpenRetailerSelector) {
                setOpen((value) => !value);
              }
            }}
            onClick={bottomNavTrigger ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              if (canOpenRetailerSelector) setOpen((value) => !value);
            } : undefined}
            title={
              iconOnly
                ? `${triggerTitle}${triggerCaption ? ` · ${triggerCaption}` : ""}`
                : bottomNavTrigger
                ? t("products.selectRetailer")
                : undefined
            }
            aria-label={bottomNavTrigger ? t("products.selectRetailer") : undefined}
            className={
              bottomNavTrigger
                ? "relative flex h-full w-full items-center justify-center text-white transition-colors duration-200 touch-manipulation"
                : iconOnly
                ? "w-full flex items-center justify-center py-1.5 rounded-lg hover:bg-white/[0.06] transition-colors cursor-pointer"
                : compact
                ? `inline-flex max-w-full items-center gap-1 rounded-full transition-all duration-200 ${
                    variant === "light"
                      ? "pl-2 pr-1 py-1 md:p-1 text-white bg-white/10 hover:bg-white/15 active:bg-white/20 border border-white/10 backdrop-blur-sm shadow-sm shadow-black/10"
                      : "px-1.5 py-0.5 md:p-1 text-primary-900 bg-white hover:bg-gray-50"
                  } ${canOpenRetailerSelector ? "cursor-pointer" : "cursor-default"}`
                : `w-full flex items-center gap-2.5 px-2 py-1.5 rounded-xl text-left bg-gradient-to-b from-white/[0.08] to-white/[0.03] border border-white/10 shadow-sm shadow-black/20 transition-all duration-200 ${
                    canOpenRetailerSelector
                      ? "cursor-pointer hover:border-white/20 hover:from-white/[0.11] hover:to-white/[0.05] active:scale-[0.99]"
                      : "cursor-default"
                  }`
            }
          >
            {bottomNavTrigger ? (
              <span className="relative flex items-center justify-center">
                <Store size={23} strokeWidth={2} />
                <ChevronDown
                  size={10}
                  strokeWidth={3}
                  className={`absolute -bottom-1 -right-2 rounded-full bg-primary-900 transition-transform duration-200 ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </span>
            ) : iconOnly ? (
              isHawkFindsActive ? (
                <Sparkles size={15} className="text-primary-300" />
              ) : isFeaturedDealsActive ? (
                <FeedGlyph icon={Flame} size={15} />
              ) : (
                <BrandDot retailer={currentValue} size={10} />
              )
            ) : compact ? (
              <>
                {isHawkFindsActive ? (
                  <Sparkles size={12} className="flex-shrink-0 text-primary-300" />
                ) : isFeaturedDealsActive ? (
                  <Flame size={12} className="flex-shrink-0 text-violet-300" />
                ) : null}
                <span className="font-semibold truncate min-w-0 text-left tracking-tight text-[13px] md:text-sm">
                  {currentLabel}
                </span>
                {canOpenRetailerSelector && (
                  <span
                    className={`flex items-center justify-center flex-shrink-0 transition-transform duration-200 h-4 w-4 rounded-full ${
                      variant === "light"
                        ? "bg-white/10 text-white/90"
                        : "border border-primary-700/50 dark:border-dark-600/60 bg-black/10 dark:bg-white/5 text-primary-400 dark:text-dark-400"
                    } ${open ? "rotate-180" : ""}`}
                  >
                    <ChevronDown size={11} strokeWidth={2.5} />
                  </span>
                )}
              </>
            ) : (
              <>
                {isHawkFindsActive ? (
                  <span aria-hidden className="flex w-4 items-center justify-center flex-shrink-0">
                    <Sparkles size={15} className="text-primary-300" />
                  </span>
                ) : isFeaturedDealsActive ? (
                  <FeedGlyph icon={Flame} size={15} />
                ) : (
                  <BrandDot retailer={currentValue} size={9} />
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-white truncate tracking-tight leading-tight">
                    {triggerTitle}
                  </span>
                  {triggerCaption && (
                    <span className="block text-[10px] font-medium text-primary-400/80 dark:text-dark-400 uppercase tracking-wider leading-tight mt-0.5">
                      {triggerCaption}
                    </span>
                  )}
                </span>
                {canOpenRetailerSelector && (
                  <span
                    className={`flex h-5 w-5 items-center justify-center flex-shrink-0 rounded-full bg-white/[0.06] text-primary-300 dark:text-dark-400 transition-transform duration-200 ${
                      open ? "rotate-180" : ""
                    }`}
                  >
                    <ChevronDown size={13} strokeWidth={2.5} />
                  </span>
                )}
              </>
            )}
          </button>
        </div>

        {/* Desktop dropdown */}
        {open && canOpenRetailerSelector && (
          <div className={`hidden md:block absolute z-50 pb-1.5 bg-primary-950/95 dark:bg-dark-800/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-selector-pop ${iconOnly ? "left-full top-0 ml-2 w-[440px]" : "left-0 top-full mt-1 w-[440px] max-w-[calc(100vw-1rem)]"}`}>
            <RetailerModeTabs />
            <div className="px-1.5 pt-1 grid [grid-template-columns:repeat(auto-fill,minmax(128px,1fr))] gap-0.5 max-h-[58vh] overflow-y-auto overscroll-contain">
              {unlockedRetailers.map((option, i) => (
                <RetailerRow key={option.value} option={option} index={i} />
              ))}
              {lockedRetailers.length > 0 && unlockedRetailers.length > 0 && <MoreStoresHeader />}
              {lockedRetailers.map((option, i) => (
                <RetailerRow key={option.value} option={option} index={unlockedRetailers.length + i} />
              ))}
            </div>
            <div className="mx-3.5 my-1.5 border-b border-white/[0.07]" />
            <p className="px-3.5 pb-1 text-[9px] font-semibold text-primary-400/60 dark:text-dark-500 uppercase tracking-widest">
              {t("products.feedsLabel")}
            </p>
            <div className="px-1.5">
              <FeaturedDealsItem />
            </div>
            {/* Flips disabled for now */}
            {/* {isAdmin && <ArbitrageItem />} */}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      {open && canOpenRetailerSelector && createPortal(
        <div
          className={`fixed inset-0 z-[999999] md:hidden transition-colors duration-200 ${sheetVisible ? "bg-black/60" : "bg-black/0"}`}
          onPointerDown={(e) => {
            e.stopPropagation();
            closeSheet();
          }}
        >
          <div
            className={`absolute bottom-0 left-0 right-0 bg-primary-950 dark:bg-dark-800 rounded-t-3xl shadow-2xl min-h-[50vh] max-h-[85vh] overflow-hidden flex flex-col transition-transform duration-200 ease-out ${
              sheetVisible ? "translate-y-0" : "translate-y-full"
            }`}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
            }}
            onTouchEnd={(e) => {
              const start = swipeStartRef.current;
              swipeStartRef.current = null;
              if (!start) return;
              const touch = e.changedTouches[0];
              const dx = touch.clientX - start.x;
              const dy = touch.clientY - start.y;
              // Horizontal-dominant swipe switches tabs; vertical stays free for scrolling
              if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                setActiveMode(dx < 0 ? "online" : "in_store");
                sheetListRef.current?.scrollTo(0, 0);
              }
            }}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-2">
              <h3 className="text-lg font-bold text-white tracking-tight">{t("products.selectRetailer")}</h3>
              <button
                onPointerDown={closeSheet}
                className="p-1.5 text-primary-400 hover:text-white rounded-full bg-white/[0.06] hover:bg-white/10 transition-colors cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <RetailerModeTabs size="large" />

            {/* Retailer list */}
            <div ref={sheetListRef} className="py-2 px-4 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-1.5">
                {unlockedRetailers.map((option, i) => (
                  <RetailerRow key={option.value} option={option} size="large" index={i} />
                ))}
                {lockedRetailers.length > 0 && unlockedRetailers.length > 0 && <MoreStoresHeader size="large" />}
                {lockedRetailers.map((option, i) => (
                  <RetailerRow key={option.value} option={option} size="large" index={unlockedRetailers.length + i} />
                ))}
              </div>
              <p className="mt-4 mb-1 px-1 text-[10px] font-semibold text-primary-400/60 dark:text-dark-500 uppercase tracking-widest">
                {t("products.feedsLabel")}
              </p>
              <FeaturedDealsItem size="large" />
              {/* Flips disabled for now */}
              {/* {isAdmin && <ArbitrageItem size="large" />} */}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center z-[999999] p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}
          onPointerDown={() => setShowUpgradeModal(false)}
        >
          <div
            className="max-w-md w-full rounded-2xl overflow-hidden bg-white dark:bg-dark-800 border border-gray-200/80 dark:border-dark-700 shadow-2xl"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-5 bg-violet-700">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                <Lock size={12} />
                {t("products.premiumLabel")}
              </div>
              <h3 className="mt-3 text-2xl font-bold text-white">{upgradeTitle}</h3>
              <p className="mt-2 text-sm text-white/90 leading-relaxed">{upgradeMessage}</p>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-2 text-sm text-gray-700 dark:text-dark-200">
                <p className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 text-emerald-500 flex-shrink-0" />
                  {t("products.accessMoreRetailers")}
                </p>
                <p className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 text-emerald-500 flex-shrink-0" />
                  {t("products.unlockFavoritesAlerts")}
                </p>
              </div>
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  onPointerDown={() => setShowUpgradeModal(false)}
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-dark-200 bg-gray-100 dark:bg-dark-700 rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onPointerDown={() => handleUpgradeClick()}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-violet-700 rounded-xl hover:bg-violet-800 transition-colors shadow-lg shadow-violet-900/25"
                >
                  {t("products.viewPlans")}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Paid plans: what they tapped and what the next plan changes */}
      <PlanUpgradeSheet
        locked={planSheetItem}
        lockedName={planSheetItem && planSheetItem !== BEST_DEALS_ITEM ? optionLabel(planSheetItem as ProductType) : undefined}
        userRetailers={user.retailers}
        onClose={closePlanSheet}
        onUpgrade={handleUpgradeClick}
      />

      {/* Home Depot Modal */}
      {showHomeDepotModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[999999] p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
          onPointerDown={() => setShowHomeDepotModal(false)}
        >
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-md w-full p-6" onPointerDown={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("products.hdPlanRequired")}</h3>
            <p className="text-sm text-gray-600 dark:text-dark-300 mb-6">{t("products.hdPlanRequiredMessage")}</p>
            <div className="flex gap-3 justify-end">
              <button onPointerDown={() => setShowHomeDepotModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 bg-gray-100 dark:bg-dark-700 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors">{t("common.cancel")}</button>
              <button onPointerDown={() => { setShowHomeDepotModal(false); navigate("/settings"); }} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors">{t("products.viewPlans")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
